import { Link, useLocation } from "@tanstack/react-router";
import {
  Banknote,
  Boxes,
  CalendarCheck,
  CalendarRange,
  Flag,
  Gauge,
  Layers,
  Layers3,
  LayoutGrid,
  Radio,
  ShieldCheck,
  SlidersHorizontal,
  Tags,
  UserLock,
  Users,
} from "lucide-react";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { ProtectedContentNotice, ViewOnlyGate } from "@/components/shared/ViewOnlyGate";
import { useCan } from "@/hooks/use-can";
import { useSession } from "@/lib/auth-client";
import { SETTINGS_TABS, visibleSettingsTabs } from "@/lib/nav-access";
import { cn } from "@/lib/utils";

// Tab definitions (label, href, write capability, protected content) live in
// src/lib/nav-access.ts so the route guards read the same list; only the icons are here.
const TAB_ICONS: Partial<Record<string, typeof Users>> = {
  "/dashboard/settings/users": Users,
  "/dashboard/settings/disciplines": ShieldCheck,
  "/dashboard/settings/pod-composition": LayoutGrid,
  "/dashboard/settings/tiers": Layers3,
  "/dashboard/settings/constraints": Layers,
  "/dashboard/settings/intensity": Gauge,
  "/dashboard/settings/request-priorities": Flag,
  "/dashboard/settings/labor-categories": Tags,
  "/dashboard/settings/rate-card": Banknote,
  "/dashboard/settings/planning": CalendarRange,
  "/dashboard/settings/internal-rates": UserLock,
  "/dashboard/settings/workstream-templates": Boxes,
  "/dashboard/settings/system": SlidersHorizontal,
  "/dashboard/settings/rundown": CalendarCheck,
  "/dashboard/settings/signals": Radio,
};

export function SettingsLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  // Which tabs a role is shown (Users, Internal Rates, and — for SA, 2026-10-20 — every
  // tab it can only read) is decided in nav-access.ts; the settings route guard agrees.
  const { data: session } = useSession();
  const visibleTabs = visibleSettingsTabs(session?.user?.role);
  const activeTab = SETTINGS_TABS.find((tab) => tab.href === location.pathname);
  const canSeeProtected = useCan(activeTab?.protectedFor?.capability ?? "viewSolutionsDashboard");

  return (
    <PageContainer>
      {/* Settings header — the only page title on any Settings tab. */}
      <PageHeader
        eyebrow="System"
        title="Settings"
        description="Manage your workspace configuration."
      />

      {/* Tab navigation */}
      <div className="relative">
        <div className="-mx-6 px-6 overflow-x-auto md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <nav className="flex gap-1 whitespace-nowrap">
            {visibleTabs.map((tab) => {
              const isActive = location.pathname === tab.href;
              const Icon = TAB_ICONS[tab.href] ?? SlidersHorizontal;

              return (
                <Link
                  key={tab.href}
                  to={tab.href}
                  role="tab"
                  aria-selected={isActive}
                  className={cn(
                    "group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors rounded-md",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/80",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-4 w-4 transition-colors",
                      isActive
                        ? "text-primary"
                        : "text-muted-foreground/60 group-hover:text-muted-foreground",
                    )}
                  />
                  <span>{tab.label}</span>

                  {/* Active indicator — warm amber underline */}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Baseline border */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
      </div>

      {/* Tab content */}
      {activeTab?.protectedFor !== undefined && !canSeeProtected ? (
        <ProtectedContentNotice>{activeTab.protectedFor.notice}</ProtectedContentNotice>
      ) : (
        <ViewOnlyGate capability={activeTab?.write ?? null}>{children}</ViewOnlyGate>
      )}
    </PageContainer>
  );
}
